import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";

import { trackEvent } from "~/lib/analytics";
import { cn } from "~/lib/utils";

import { buttonVariants } from "./button-utils";

export interface ButtonProps
    extends React.ButtonHTMLAttributes<HTMLButtonElement>,
        VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    isLoading?: boolean;
    analyticsId?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {
            className,
            variant,
            size,
            asChild = false,
            isLoading = false,
            children,
            analyticsId,
            onClick,
            ...props
        },
        ref,
    ) => {
        const Comp = asChild ? Slot : "button";

        const handleClick = React.useCallback(
            (
                e:
                    | React.MouseEvent<HTMLButtonElement>
                    | React.TouchEvent<HTMLButtonElement>,
            ) => {
                // AIDEV-NOTE: Handle both click and touch events for mobile compatibility
                // Get button text or icon name
                let buttonText = analyticsId;
                let iconType = "";

                if (!buttonText) {
                    if (typeof children === "string") {
                        buttonText = children;
                    } else {
                        // Check if children is a single element
                        try {
                            const child = React.Children.only(children);
                            if (React.isValidElement(child)) {
                                // Check for data-component-name attribute
                                iconType =
                                    child.props["data-component-name"] ||
                                    "unknown";
                            } else {
                                buttonText =
                                    e.currentTarget.textContent || "unknown";
                            }
                        } catch (error) {
                            // wasn't an only child
                            buttonText =
                                e.currentTarget.textContent || "unknown";
                        }
                    }
                }

                trackEvent("clicked button", {
                    text: analyticsId || buttonText || iconType,
                    icon: iconType,
                });

                // Call the original onClick handler if it exists
                return onClick?.(e as React.MouseEvent<HTMLButtonElement>);
            },
            [children, analyticsId, onClick],
        );

        return (
            <Comp
                className={cn(buttonVariants({ variant, size, className }))}
                ref={ref}
                disabled={isLoading || props.disabled}
                onClick={handleClick}
                onTouchEnd={handleClick}
                {...props}
            >
                {isLoading ? (
                    <>
                        <Loader2 className="mr-2 size-4 animate-spin" />
                        {children}
                    </>
                ) : (
                    children
                )}
            </Comp>
        );
    },
);
Button.displayName = "Button";

export { Button, buttonVariants };
